import {
  Refine,
  Authenticated,
} from "@refinedev/core";

import {
  AuthPage,
  ThemedLayout,
} from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import "@ds/tokens/ds-utilities.css";

import { dataProvider as supabaseDataProvider, liveProvider } from "@refinedev/supabase";
import { createAppDataProvider } from "./providers/appDataProvider";
import { buildAppTransforms } from "./providers/appTransforms";
import { App as AntdApp, ConfigProvider } from "antd";
import { BrowserRouter, Route, Routes, Outlet, useLocation } from "react-router";
import { useEffect } from "react";
import {
  NavigateToResource,
  CatchAllNavigate,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router";
import { appRouterProvider } from "./providers/appRouterProvider";
import { useTranslation } from "react-i18next";

// Ant Design locales
import plPL from "antd/locale/pl_PL";
import enUS from "antd/locale/en_US";

// Import i18n configuration
import "./i18n";

import { AppIcon } from "@components/app-icon";
import { supabaseClient } from "./utility/supabaseClient";
import { useCustomNotificationProvider } from "./utility/notificationProvider";
import { antdTheme } from "@ds/tokens/antdTheme";
import { ErrorBoundary } from "@components/ErrorBoundary";
import { UpdatePrompt } from "@components/UpdatePrompt";
import { Header } from "@components/header";
import authProvider from "./authProvider";
import React, { Suspense } from "react";
import { Spin } from "antd";
import { RealtimeProvider } from "./contexts/RealtimeContext";
import { ScanJobWatcher } from "@entities/pipeline/ScanJobWatcher";
import { TabsProvider } from "./router/TabsContext";

// Dayjs setup
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "dayjs/locale/pl";
import "dayjs/locale/en";
import {
  AimOutlined,
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  CheckSquareOutlined,
  CarOutlined,
  CloudOutlined,
  ContactsOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  FilterOutlined,
  HomeOutlined,
  InboxOutlined,
  MailOutlined,
  RobotOutlined,
  SettingOutlined,
  ShopOutlined,
  SlidersOutlined,
  SwapOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserSwitchOutlined,
  PercentageOutlined,
  OrderedListOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { resourceDefs } from "./router/resourceDefs";

// resourceDefs is pure data (node tooling — the e2e-visual crawler — imports it
// to enumerate routes), so the icon JSX is attached here.
const RESOURCE_ICONS: Record<string, React.ReactNode> = {
  AimOutlined: <AimOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  ArrowDownOutlined: <ArrowDownOutlined />,
  ArrowUpOutlined: <ArrowUpOutlined />,
  AuditOutlined: <AuditOutlined />,
  BankOutlined: <BankOutlined />,
  BarChartOutlined: <BarChartOutlined />,
  CheckSquareOutlined: <CheckSquareOutlined />,
  CarOutlined: <CarOutlined />,
  CloudOutlined: <CloudOutlined />,
  ContactsOutlined: <ContactsOutlined />,
  DollarOutlined: <DollarOutlined />,
  FileDoneOutlined: <FileDoneOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  FilterOutlined: <FilterOutlined />,
  HomeOutlined: <HomeOutlined />,
  InboxOutlined: <InboxOutlined />,
  MailOutlined: <MailOutlined />,
  RobotOutlined: <RobotOutlined />,
  SettingOutlined: <SettingOutlined />,
  ShopOutlined: <ShopOutlined />,
  SlidersOutlined: <SlidersOutlined />,
  SwapOutlined: <SwapOutlined />,
  TeamOutlined: <TeamOutlined />,
  ThunderboltOutlined: <ThunderboltOutlined />,
  UserSwitchOutlined: <UserSwitchOutlined />,
  PercentageOutlined: <PercentageOutlined />,
  OrderedListOutlined: <OrderedListOutlined />,
  WarningOutlined: <WarningOutlined />,
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
};

const resources = resourceDefs.map(({ meta, ...paths }) => ({
  ...paths,
  ...(meta
    ? { meta: { ...meta, icon: meta.icon ? RESOURCE_ICONS[meta.icon] : undefined } }
    : {}),
}));
// Mobile upload is the only page rendered OUTSIDE the themed shell / tab
// system — it lives in its own authenticated sibling route. Everything
// else is rendered through `<TabsOutlet/>` in TabbableRoutes.
const MobileUploadPage = React.lazy(() =>
  import("./pages/mobile/MobileUploadPage").then((m) => ({ default: m.MobileUploadPage })),
);


// ── Eager imports (always needed) ───────────────────────────────────────
import { GoogleCallback } from "@services/google/GoogleCallback";
import { CustomSider } from "@components/CustomSider";
import { ActionProvider } from "@ds/actions/ActionContext";
import { BackgroundWorkMonitor } from "@components/actions/BackgroundWorkMonitor";
import { ProcessProvider, ProcessFixModalSlot } from "@ds/actions/ProcessContext";
import { EmployeeProvider } from "./contexts/EmployeeContext";
import { EmployeeGate } from "@components/EmployeeGate";
import { GoogleScopeBanner } from "@components/GoogleScopeBanner";
import { TabbableRoutes } from "./router/TabbableRoutes";
import { BreadcrumbBar } from "./router/BreadcrumbBar";
import { ConfirmProvider } from "@ds/connected/ConfirmProvider";
import { EntityLinkProvider } from "@ds/connected/EntityLinkProvider";
import { EntitySelectProvider } from "@ds/connected/EntitySelectProvider";
import { Sentry } from "./sentry";
import { setGlobalSearchFilterBackend } from "@ds/tables/helpers/globalSearchBackend";
import { buildGlobalSearchFilter } from "./hooks/useGlobalSearchFilter";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Register the concrete global-search backend for the ds table-search seam.
// ds declares the seam (`@ds/tables/helpers/globalSearchBackend`) but the real
// impl calls the `global_search` RPC (app-side data access), so it is injected
// here at module scope — before any table can render.
setGlobalSearchFilterBackend(buildGlobalSearchFilter);

function SentryRouteContext() {
  const location = useLocation();
  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const resource = parts[0] ?? "unknown";
    const maybeId = parts.find((p, i) => i > 0 && /^[0-9a-f-]{8,}$|^\d+$/.test(p));
    Sentry.setTag("resource", resource);
    Sentry.setTag("record.id", maybeId ?? "");
  }, [location.pathname]);
  return null;
}

function App() {
  const { i18n } = useTranslation();

  // Map i18n language to Ant Design locale
  const antdLocale = i18n.language === "pl" ? plPL : enUS;

  // Update dayjs locale when language changes
  dayjs.locale(i18n.language);

  return (
    <BrowserRouter>
      <style>{`html, body { overscroll-behavior-x: none; } .hide-header > .ant-page-header-heading { display: none; } .ant-message { z-index: 99999 !important; } .ant-notification { z-index: 99999 !important; } .ant-layout-sider-children { display: flex; flex-direction: column; min-height: 0; } .sider-scroll { scrollbar-width: thin; scrollbar-color: #26304a #161c2c; } .sider-scroll::-webkit-scrollbar { width: 6px; } .sider-scroll::-webkit-scrollbar-track { background: #161c2c; } .sider-scroll::-webkit-scrollbar-thumb { background: #26304a; border-radius: 3px; } .sider-scroll::-webkit-scrollbar-thumb:hover { background: #3a4660; } .ant-layout.ant-layout-has-sider { height: 100vh; overflow: hidden; } .ant-layout-has-sider > .ant-layout { min-height: 0; } .ant-layout-content { padding: 0 !important; display: flex; flex-direction: column; min-height: 0; border-left: 1px solid rgba(5, 5, 5, 0.06); } .ant-layout-content > div { padding: 0 !important; min-height: 0 !important; flex: 1; display: flex; flex-direction: column; }
.ant-select-sm.ant-select-multiple .ant-select-selector {
  min-height: 24px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}`}</style>
          <ConfigProvider locale={antdLocale} theme={antdTheme}>
            <AntdApp message={{ maxCount: 3, top: 24 }} notification={{ placement: "topRight" }}>
              <ConfirmProvider>
              <UpdatePrompt />
              <ErrorBoundary>
                <ActionProvider>
                <ProcessProvider>
                <Refine
                  dataProvider={createAppDataProvider(supabaseDataProvider(supabaseClient), buildAppTransforms())}
                  liveProvider={liveProvider(supabaseClient)}
                  authProvider={authProvider}
                  routerProvider={appRouterProvider}
                  notificationProvider={useCustomNotificationProvider}
                  resources={resources}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    projectId: "DQ2ggC-svPuB1-To3zTg",
                    title: { text: "IOOO Transactions", icon: <AppIcon /> },
                  }}
                >
                  <EntityLinkProvider>
                  <EntitySelectProvider>
                  <EmployeeProvider>
                  <TabsProvider>
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="authenticated-inner"
                          fallback={<CatchAllNavigate to="/login" />}
                        >
                            <EmployeeGate>
                              <ThemedLayout
                                Header={Header}
                                Sider={(props) => <CustomSider {...props} />}
                              >
                                <SentryRouteContext />
                                <GoogleScopeBanner />
                                <RealtimeProvider>
                                <ScanJobWatcher />
                                <BreadcrumbBar />
                                <Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}><Spin size="large" /></div>}>
                                  <Outlet />
                                </Suspense>
                                </RealtimeProvider>
                              </ThemedLayout>
                            </EmployeeGate>
                        </Authenticated>
                      }
                    >
                      {/* No-tab lifecycle routes render directly under the shell. */}
                      <Route path="/google-callback" element={<GoogleCallback />} />
                      <Route path="/auth/callback" element={<NavigateToResource />} />
                      {/* Everything else renders through the tab system. */}
                      <Route path="*" element={<TabbableRoutes />} />
                    </Route>


                    {/* Mobile upload — authenticated, no desktop layout */}
                    <Route path="/m/upload/:token?" element={
                      <Authenticated key="mobile-auth" fallback={<CatchAllNavigate to="/login" />}>
                        <EmployeeGate>
                          <MobileUploadPage />
                        </EmployeeGate>
                      </Authenticated>
                    } />

                    <Route
                      element={
                        <Authenticated
                          key="authenticated-outer"
                          fallback={<Outlet />}
                        >
                          <NavigateToResource />
                        </Authenticated>
                      }
                    >
                      <Route
                        path="/login"
                        element={
                          <AuthPage
                            type="login"
                            // Show email/password form ONLY when the e2e build
                            // flag is set. Production builds keep OAuth-only.
                            hideForm={import.meta.env.VITE_E2E_AUTH !== "1"}
                            registerLink={false}
                            title="Logowanie"
                            providers={[
                              {
                                name: "google",
                                label: "Zaloguj się",

                              }
                            ]}

                            formProps={{}}
                          />
                        }
                      />
                      <Route
                        path="/register"
                        element={<AuthPage type="register" />}
                      />
                      <Route
                        path="/forgot-password"
                        element={<AuthPage type="forgotPassword" />}
                      />
                    </Route>
                  </Routes>
                  </TabsProvider>
                  </EmployeeProvider>
                  <UnsavedChangesNotifier />
                  <DocumentTitleHandler />
                  <ProcessFixModalSlot />
                  </EntitySelectProvider>
                  </EntityLinkProvider>
                </Refine>
                <BackgroundWorkMonitor />
                </ProcessProvider>
                </ActionProvider>
              </ErrorBoundary>
              </ConfirmProvider>
            </AntdApp>
          </ConfigProvider>
    </BrowserRouter>
  );
}

export default App;